import { useEffect, useState } from "react";
import { findSimilarCases } from "../lib/admin.js";
import { SYMPTOM_LABEL_MAP } from "../lib/symptoms.js";

// 작업/회고 화면에서 "비슷한 발행 케이스 5개"를 보여주는 재사용 카드.
// 클릭하면 회고 페이지(#admin/archive/<id>)로 이동한다. 운영자는 새 탭이
// 아니라 같은 탭에서 열고 브라우저 뒤로 가기로 돌아오는 흐름.
//
// props.title을 바꾸면 헤더만 다르게 노출 가능.

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months = (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

export default function SimilarCases({ caseId, title = "🔗 비슷한 발행 케이스", limit = 5 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    findSimilarCases({ caseId, limit })
      .then((res) => { if (!cancelled) { setRows(res); setLoading(false); } })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message ?? "불러오기 실패");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [caseId, limit]);

  return (
    <div style={{background:'#fff',borderRadius:14,padding:'14px 16px',boxShadow:'var(--shadow-sm)',marginBottom:12}}>
      <div style={{fontSize:11,fontWeight:800,color:'#4a5568',textTransform:'uppercase',letterSpacing:.4,marginBottom:10}}>
        {title}
      </div>

      {loading ? (
        <div style={{padding:'14px 0',textAlign:'center'}}>
          <div className="spinner" style={{display:'inline-block'}}/>
        </div>
      ) : error ? (
        <div style={{fontSize:12,color:'#c0392b',lineHeight:1.5}}>
          비슷한 케이스를 불러오지 못했어요: {error}
        </div>
      ) : rows.length === 0 ? (
        <div style={{fontSize:12,color:'#718096',lineHeight:1.5}}>
          공통 신호를 가진 과거 발행 케이스가 아직 없어요.
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {rows.map((r) => {
            const age = formatAge(r.child?.birth_month);
            const tagPills = r.commonTags.slice(0, 3);
            const symptomLabels = r.commonSymptoms
              .slice(0, 3)
              .map((k) => SYMPTOM_LABEL_MAP[k] ?? k);
            return (
              <a
                key={r.id}
                href={`#admin/archive/${r.id}`}
                style={{
                  display:'block',padding:'10px 12px',background:'#fafafa',
                  borderRadius:10,border:'1px solid #e2e8f0',
                  textDecoration:'none',color:'inherit',
                }}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,gap:8}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#1a1a2e',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {r.child?.name ?? "(이름 없음)"}
                    {age && <span style={{color:'#718096',fontWeight:500}}> · {age}</span>}
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
                    <span style={{fontSize:10,color:'#2c5f8d',fontWeight:800}}>
                      ★ {r.score}
                    </span>
                    <span style={{fontSize:10,color:'#a0aec0'}}>
                      {formatDate(r.sent_at)}
                    </span>
                  </div>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                  {tagPills.map((t) => (
                    <span key={`t-${t}`} style={{
                      fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:9999,
                      background:'#2c5f8d18',color:'#2c5f8d',
                    }}>
                      {t}
                    </span>
                  ))}
                  {symptomLabels.map((l) => (
                    <span key={`s-${l}`} style={{
                      fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:9999,
                      background:'#9b89b318',color:'#5e4a7f',
                    }}>
                      {l}
                    </span>
                  ))}
                  {(r.commonTags.length > tagPills.length || r.commonSymptoms.length > symptomLabels.length) && (
                    <span style={{fontSize:10,color:'#a0aec0',padding:'2px 4px'}}>
                      +{(r.commonTags.length - tagPills.length) + (r.commonSymptoms.length - symptomLabels.length)}
                    </span>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      )}

      <div style={{marginTop:8,fontSize:10,color:'#a0aec0',lineHeight:1.5}}>
        ★ 점수: 같은 증상 카테고리 +2, 체크된 세부 증상 +1
      </div>
    </div>
  );
}
